import React, { useEffect, useState } from 'react';
import { Loader2, UserPlus, Crosshair } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { firmasAPI } from '../../api';
import { toast } from '../../hooks/use-toast';
import ColocarCasillasDialog from './ColocarCasillasDialog';
import EnlaceFirmante from './EnlaceFirmante';

// Añade un firmante a una petición ya creada, aunque algún firmante ya haya firmado.
const AnadirFirmanteDialog = ({ open, onOpenChange, baseFirmaId, adjuntoId, clientes, maxPaginas, onDone }) => {
  const [clienteId, setClienteId] = useState('');
  const [modo, setModo] = useState('email');
  const [emailDestino, setEmailDestino] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [casilla, setCasilla] = useState(null);
  const [colocando, setColocando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (open) {
      setClienteId(clientes.length === 1 ? String(clientes[0].id) : '');
      setModo('email');
      setEmailDestino('');
      setMensaje('');
      setCasilla(null);
      setResultado(null);
    }
  }, [open, clientes]);

  const cliente = clientes.find((c) => String(c.id) === String(clienteId)) || null;
  const presencial = modo === 'despacho';

  const enviar = async () => {
    if (!cliente || !casilla) return;
    setEnviando(true);
    try {
      const res = await firmasAPI.anadirFirmante(baseFirmaId, {
        cliente_id: cliente.id,
        pagina: casilla.pagina,
        rect_rel: casilla.rect_rel,
        email_destino: presencial ? '' : emailDestino.trim(),
        enviar_email: !presencial,
        presencial,
        mensaje: mensaje.trim(),
      });
      setResultado({ ...res.data, presencial });
      toast({
        title: res.data?.duplicado_existente
          ? 'Ese firmante ya tenía una petición pendiente'
          : `${cliente.nombre} añadido a la firma conjunta`,
        description: res.data?.email_enviado
          ? `Enlace enviado a ${res.data.email_enviado_a}`
          : 'Comparte el enlace o pasa la tablet',
      });
      window.dispatchEvent(new Event('lexduo:firmas'));
      onDone?.();
    } catch (e) {
      toast({ title: 'No se pudo añadir el firmante',
              description: e?.response?.data?.detail || 'Error inesperado', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const listaCasilla = cliente
    ? [{ cliente_id: cliente.id, nombre: cliente.nombre, pagina: casilla?.pagina, rect_rel: casilla?.rect_rel }]
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="anadir-firmante-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="w-4 h-4 text-indigo-600" /> Añadir otro firmante
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Su casilla se aplicará sobre la versión vigente del documento, con las firmas que ya se hayan hecho.
          </DialogDescription>
        </DialogHeader>

        {resultado ? (
          <EnlaceFirmante firmante={resultado} />
        ) : (
          <div className="space-y-3">
            {clientes.length === 0 ? (
              <div className="p-3 rounded bg-amber-50 text-amber-900 text-sm" data-testid="anadir-sin-clientes">
                Todos los clientes del expediente están ya en esta firma conjunta.
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <Label className="text-sm">Firmante</Label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                    value={clienteId}
                    onChange={(e) => { setClienteId(e.target.value); setCasilla(null); }}
                    data-testid="anadir-cliente-select"
                  >
                    <option value="">— Elige al cliente —</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.nombre}{c.rol ? ` · ${c.rol.replace('_', ' ')}` : ''}
                        {c.tiene_nif ? ` · DNI ${c.nif_parcial}` : ' · sin DNI/NIF'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setModo('email')}
                          className={`rounded-md border px-3 py-2 text-sm text-left ${
                            presencial ? 'border-gray-200 bg-white' : 'border-indigo-500 bg-indigo-50'}`}
                          data-testid="anadir-modo-email">Enviar por email</button>
                  <button type="button" onClick={() => setModo('despacho')}
                          className={`rounded-md border px-3 py-2 text-sm text-left ${
                            presencial ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white'}`}
                          data-testid="anadir-modo-despacho">Firmar en el despacho</button>
                </div>

                {!presencial && (
                  <>
                    <Input
                      placeholder={cliente?.email ? `Email de la ficha: ${cliente.email}` : 'cliente@dominio.com'}
                      value={emailDestino}
                      onChange={(e) => setEmailDestino(e.target.value)}
                      data-testid="anadir-email-input"
                    />
                    <Input
                      placeholder="Mensaje opcional"
                      value={mensaje}
                      onChange={(e) => setMensaje(e.target.value)}
                      data-testid="anadir-mensaje-input"
                    />
                  </>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" disabled={!cliente}
                          onClick={() => setColocando(true)} data-testid="anadir-colocar-btn">
                    <Crosshair className="w-4 h-4 mr-2" /> Colocar su casilla
                  </Button>
                  <span className={`text-xs ${casilla ? 'text-indigo-700' : 'text-amber-700'}`}
                        data-testid="anadir-casilla-estado">
                    {casilla ? `Casilla en la página ${casilla.pagina + 1}` : 'Obligatorio: coloca su casilla'}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="anadir-cerrar-btn">
            {resultado ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!resultado && (
            <Button onClick={enviar} disabled={!cliente || !casilla || enviando} data-testid="anadir-confirmar-btn">
              {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Añadir firmante
            </Button>
          )}
        </DialogFooter>

        <ColocarCasillasDialog
          open={colocando}
          onOpenChange={setColocando}
          adjuntoId={adjuntoId}
          firmantes={listaCasilla}
          maxPaginas={maxPaginas}
          clientePreviewId={cliente?.id}
          onColocar={(_cid, pos) => setCasilla(pos)}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AnadirFirmanteDialog;
